import type { ActionContext, ActionDependencies, StateData } from '@/services/action/types'
import { mergePageFieldMaps } from '@/common/utils/mergePageFieldMaps'
import {
  resolveClientDataActualFields,
  resolveJsResultChangedDataFieldMap,
} from '@/common/utils/resolveClientDataActualFields'
import { mergeLiveFieldOverridesIntoFlatFormData } from '@/common/utils/liveFormFieldOverrides'
import { isDialogCloseParentMergeBlockedKey } from '@/services/action/actionUtils'
import { isClientOnlyUiMutationScript } from '@/services/action/actionScriptRuntime'
import { mergeFieldMaps } from '@/common/utils/jsonUniData.ts'

export type DialogCloseMergeScopeContext = Pick<
  ActionContext,
  'formId' | 'panelId' | 'elementId' | 'pageId' | 'level' | 'data'
> & {
  actionItem?: any
}

function layoutHost(ctx: DialogCloseMergeScopeContext): any {
  if (ctx.data && typeof ctx.data === 'object' && Object.keys(ctx.data).length > 0) {
    return ctx.data
  }
  if (ctx.actionItem && typeof ctx.actionItem === 'object') {
    return ctx.actionItem
  }
  return ctx.data || {}
}

function normalizeLayoutText(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'object' && v !== null && '_text' in (v as any)) {
    return String((v as { _text?: unknown })._text ?? '').trim().toLowerCase()
  }
  return String(v).trim().toLowerCase()
}

/** Поля, которые не фильтруются при merge scope диалога (сейчас пусто). */
const MERGE_PASSTHROUGH_FIELD_KEYS = new Set<string>()

export function isNarrowDialogParentMergeLayoutLevel(layoutLevel: unknown): boolean {
  const l = String(layoutLevel ?? '').trim().toLowerCase()
  return l === 'element' || l === 'panel'
}

export const DIALOG_CLOSE_MERGE_SCOPE_CLIENT_CONFIG_KEYS = [
  'onCloseDialogParentMergeFieldKeys',
  'onCloseDialogParentMergeLayoutLevel',
] as const

export function omitDialogCloseMergeScopeFromConfig(
  cfg: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) {
    return {}
  }
  const out = { ...cfg }
  for (const k of DIALOG_CLOSE_MERGE_SCOPE_CLIENT_CONFIG_KEYS) {
    delete out[k]
  }
  return out
}

export function buildDialogCloseConfigForStore(
  responseCfg: Record<string, unknown> | undefined | null,
  overrides: Record<string, unknown> = {},
): Record<string, unknown> {
  const base =
    responseCfg && typeof responseCfg === 'object' && !Array.isArray(responseCfg)
      ? { ...responseCfg }
      : {}
  return { ...base, ...overrides }
}

export function hasDialogCloseMergeFieldKeys(cfg: Record<string, unknown> | undefined | null): boolean {
  const keysRaw = cfg?.onCloseDialogParentMergeFieldKeys
  return Array.isArray(keysRaw) && keysRaw.length > 0
}

/** Поля-источники из getFieldAsTable("…") в event.dialogClosed. */
export function inferDialogCloseSourceTableFieldsFromScript(script: unknown): string[] {
  const text = String(script ?? '').trim()
  if (!text) return []
  const keys = new Set<string>()
  const re = /getFieldAsTable\(\s*["']([^"']+)["']/g
  for (const match of text.matchAll(re)) {
    const key = String(match[1] ?? '').trim()
    if (key) keys.add(key)
  }
  return [...keys]
}

export function collectFieldsFromElementRegistry(
  elements: Record<string, unknown> | undefined,
  registryFormId: string,
  fieldNames: string[],
  options?: { slotFormIds?: string[] },
): Record<string, unknown> {
  if (!elements || !fieldNames.length) return {}
  const wanted = new Set(fieldNames.map((k) => String(k)))
  const slotIds = new Set(
    [registryFormId, ...(options?.slotFormIds ?? [])]
      .map((id) => String(id ?? '').trim())
      .filter(Boolean),
  )
  const out: Record<string, unknown> = {}

  for (const el of Object.values(elements)) {
    const rec = el as Record<string, unknown>
    const elementFormId = String(rec.formId ?? '').trim()
    const elementPageSlot = String(rec.pageDataSlot ?? '').trim()
    const formMatch = elementFormId !== '' && slotIds.has(elementFormId)
    const slotMatch = elementPageSlot !== '' && slotIds.has(elementPageSlot)
    if (!formMatch && !slotMatch) continue
    const fieldName = String(rec.fieldName ?? rec.name ?? '').trim()
    if (!wanted.has(fieldName)) continue
    const getData = rec.getData as ((action?: unknown) => Record<string, unknown>) | undefined
    if (typeof getData !== 'function') continue
    try {
      const data = getData()
      if (!data || typeof data !== 'object') continue
      for (const [k, v] of Object.entries(data)) {
        if (wanted.has(k)) out[k] = v
      }
    } catch {
      /* non-fatal */
    }
  }

  return out
}

function tableRowCountFromFieldEntry(entry: unknown): number {
  if (entry == null || typeof entry !== 'object') return 0
  const record = entry as Record<string, unknown>
  const raw = 'value' in record ? record.value : entry
  if (raw == null || typeof raw !== 'object') return 0
  const rows = (raw as { rows?: unknown[] }).rows
  return Array.isArray(rows) ? rows.length : 0
}

function fieldEntryHasTableRows(entry: unknown): boolean {
  return tableRowCountFromFieldEntry(entry) > 0
}

function findRegistryElementForField(
  elements: Record<string, unknown> | undefined,
  registryFormId: string,
  dialogSlotId: string,
  fieldName: string,
): Record<string, unknown> | undefined {
  if (!elements) return undefined
  const slotIds = new Set(
    [registryFormId, dialogSlotId]
      .map((id) => String(id ?? '').trim())
      .filter(Boolean),
  )
  for (const el of Object.values(elements)) {
    const rec = el as Record<string, unknown>
    const elementFormId = String(rec.formId ?? '').trim()
    const elementPageSlot = String(rec.pageDataSlot ?? '').trim()
    const formMatch = elementFormId !== '' && slotIds.has(elementFormId)
    const slotMatch = elementPageSlot !== '' && slotIds.has(elementPageSlot)
    if (!formMatch && !slotMatch) continue
    const name = String(rec.fieldName ?? rec.name ?? '').trim()
    if (name !== fieldName) continue
    return rec
  }
  return undefined
}

function resolveDialogCloseFieldFromGetForm(
  getForm: ActionDependencies['getForm'],
  fieldName: string,
  registryElement: Record<string, unknown> | undefined,
  dialogSlotId: string,
  registryFormId: string,
): unknown {
  if (typeof getForm !== 'function') return undefined

  const elementFormId = String(registryElement?.formId ?? registryFormId ?? dialogSlotId ?? '').trim()
  const panelIdRaw = registryElement?.panelId
  const elementIdRaw = registryElement?.elementId
  const panelId =
    typeof panelIdRaw === 'string' || typeof panelIdRaw === 'number'
      ? panelIdRaw
      : undefined
  const elementId =
    typeof elementIdRaw === 'string' || typeof elementIdRaw === 'number'
      ? elementIdRaw
      : undefined
  if (elementFormId && panelId != null && elementId != null) {
    for (const slotId of [dialogSlotId, registryFormId, elementFormId]) {
      if (!slotId) continue
      const scoped = getForm({
        formId: elementFormId,
        panelId,
        elementId,
        pageDataSlot: slotId,
        flatten: true,
      }) as Record<string, unknown> | undefined
      if (scoped?.[fieldName] !== undefined) {
        return scoped[fieldName]
      }
    }
  }

  for (const fid of [dialogSlotId, registryFormId]) {
    if (!fid) continue
    const flat = getForm({ formId: fid, pageDataSlot: fid, flatten: true }) as Record<string, unknown> | undefined
    if (flat?.[fieldName] !== undefined) {
      return flat[fieldName]
    }
  }

  return undefined
}

export function resolveDialogCloseSourceFieldOverlay(options: {
  elements?: Record<string, unknown>
  registryFormId: string
  dialogSlotId: string
  fieldNames: string[]
  getForm?: ActionDependencies['getForm']
}): Record<string, unknown> {
  const { elements, registryFormId, dialogSlotId, fieldNames, getForm } = options
  if (!fieldNames.length) return {}

  const out: Record<string, unknown> = {}
  for (const fieldName of fieldNames) {
    const fromRegistry = collectFieldsFromElementRegistry(elements, registryFormId, [fieldName], {
      slotFormIds: [dialogSlotId, registryFormId],
    })
    const registryEntry = fromRegistry[fieldName]
    const registryElement = findRegistryElementForField(elements, registryFormId, dialogSlotId, fieldName)
    const registryHasLiveGetData = typeof registryElement?.getData === 'function'

    if (registryEntry !== undefined && (registryHasLiveGetData || fieldEntryHasTableRows(registryEntry))) {
      out[fieldName] = registryEntry
      continue
    }

    const fromForm = resolveDialogCloseFieldFromGetForm(
      getForm,
      fieldName,
      registryElement,
      dialogSlotId,
      registryFormId,
    )

    if (fromForm !== undefined) {
      out[fieldName] = fromForm
    } else if (registryEntry !== undefined) {
      out[fieldName] = registryEntry
    }
  }

  return out
}

/** Скрипт только перезагружает форму/панели/элементы без setField/refillElement/empty. */
export function isDialogCloseReloadOnlyScript(script: unknown): boolean {
  const text = String(script ?? '').trim()
  if (!text) return false
  const hasReload =
    /(?:en\.)?reloadForm\s*\(/.test(text) ||
    /(?:en\.)?reloadPanel\s*\(/.test(text) ||
    /(?:en\.)?reloadElement\s*\(/.test(text)
  const hasDataMutation = /(?:en\.)?(?:setField|refillElement|empty)\s*\(/.test(text)
  return hasReload && !hasDataMutation
}

/**
 * After dialogClosed already applied live setVisible/reloadForm, do not replay
 * stored jsResult.states (would restore stale label visibility).
 */
export function shouldSkipDialogCloseVisibilityReplay(script: unknown): boolean {
  if (isDialogCloseReloadOnlyScript(script)) return true
  return isClientOnlyUiMutationScript(String(script ?? ''))
}

export type DialogCloseVisibilityFormProvider = {
  setVisible?: (name: string, visible: boolean) => void
  setVisibleByLevel?: (
    level: string,
    id: string | number,
    visible: boolean,
    options?: { propagateChildren?: boolean },
  ) => void
}

function isNameKeyedVisibilityId(id: string | number): boolean {
  const idStr = String(id ?? '').trim()
  return idStr !== '' && !/^\d+$/.test(idStr)
}

/** En.controls for dialogClosed: triad setVisible must hit the parent formProvider. */
export function bindDialogCloseEnVisibilityControls(
  formProvider: DialogCloseVisibilityFormProvider | null | undefined,
): DialogCloseVisibilityFormProvider {
  if (!formProvider) return {}
  return {
    setVisible: (name, visible) => {
      formProvider.setVisible?.(name, visible)
    },
    setVisibleByLevel: (level, id, visible, options) => {
      if (typeof formProvider.setVisibleByLevel === 'function') {
        formProvider.setVisibleByLevel(level, id, visible, {
          ...options,
          propagateChildren: options?.propagateChildren ?? false,
        })
      } else {
        formProvider.setVisible?.(String(id), visible)
      }
      const levelNorm = String(level ?? '').trim().toLowerCase()
      if (
        (levelNorm === 'element' || levelNorm === 'action') &&
        isNameKeyedVisibilityId(id)
      ) {
        formProvider.setVisible?.(String(id), visible)
      }
    },
  }
}

/** Keep dialogClosed setVisible queue; empty NReturn must not drop it. */
export function pickDialogClosedDispatchedVisibilities(
  dialogClosedPhase: { dispatched_visibilities?: unknown } | null | undefined,
  lastPhase: { dispatched_visibilities?: unknown } | null | undefined,
): unknown {
  const fromClosed = dialogClosedPhase?.dispatched_visibilities
  if (Array.isArray(fromClosed) && fromClosed.length > 0) return fromClosed
  return lastPhase?.dispatched_visibilities
}

function asFieldMap(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

/**
 * dialogClosed seed from the closing slot: changed_data / currentData overlay uniData
 * so post-chain scalars win over values from dialog open.
 */
export function extractDialogCloseSeedFieldMap(pageData: unknown): Record<string, unknown> {
  if (!pageData || typeof pageData !== 'object' || Array.isArray(pageData)) return {}
  const page = pageData as Record<string, any>
  const uniFields = page.uniData?.fields?.fields
  const changedFields = page.jsResult?.changed_data?.fields?.fields
  const currentFields = page.currentData?.fields?.fields ?? page.currentData?.fields
  const currentClientFields =
    page.currentData?.clientData?.fields?.fields ?? page.currentData?.clientData?.fields
  return mergeFieldMaps(
    asFieldMap(uniFields),
    asFieldMap(currentClientFields),
    asFieldMap(currentFields),
    asFieldMap(changedFields),
  )
}

/** Swing onDialogClose(incomingData): post-chain UniData of the closing action. */
export function extractIncomingDataFieldMap(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {}
  return extractDialogCloseSeedFieldMap({
    currentData: data,
    jsResult: { changed_data: data },
  })
}

/** Поля из setField/refillElement/empty/reloadElement в event.dialogClosed или form.functions. */
export function inferDialogCloseMergeFieldKeysFromScript(script: unknown): string[] {
  const text = String(script ?? '').trim()
  if (!text) return []
  const keys = new Set<string>()
  const re = /(?:en\.)?(?:setField|refillElement|empty|reloadElement)\(\s*["']([^"']+)["']/g
  for (const match of text.matchAll(re)) {
    const key = String(match[1] ?? '').trim()
    if (key) keys.add(key)
  }
  return [...keys]
}

export function stripDialogCloseSideEffectFieldsFromMergeResult(
  merged: Record<string, unknown>,
  options: { scriptText?: unknown; parentFunctions?: string | null },
): Record<string, unknown> {
  if (!merged || typeof merged !== 'object' || Array.isArray(merged)) {
    return {}
  }
  const combined = [options.parentFunctions, options.scriptText].filter(Boolean).join('\n')
  const sideEffects = new Set(inferDialogCloseMergeFieldKeysFromScript(combined))
  if (!sideEffects.size) return merged
  const out = { ...merged }
  for (const key of sideEffects) {
    delete out[key]
  }
  return out
}

export function readRegisteredElementOnChangeScript(reg: unknown): string {
  if (!reg || typeof reg !== 'object') return ''
  const record = reg as Record<string, unknown>
  const raw = record.onChange ?? record['event.onChange']
  if (typeof raw === 'string') return raw.trim()
  if (raw && typeof raw === 'object' && '_text' in (raw as object)) {
    return String((raw as { _text?: unknown })._text ?? '').trim()
  }
  return ''
}

export type DialogCloseFieldOnChangeContext = {
  fieldName?: string
  parentStoreKey?: string
  parentPageData?: Record<string, unknown>
  formId?: string
  panelId?: string | number
  elementId?: string | number
  element?: Record<string, unknown>
}

export type DialogClosePendingFieldOnChange = {
  fieldName: string
  fieldData: unknown
  registered?: Record<string, unknown>
  context: DialogCloseFieldOnChangeContext
}

type DialogCloseFormProvider = {
  onElement?: (ctx: Record<string, unknown>) => void | Promise<void>
  runAction?: (ctx: Record<string, unknown>) => void | Promise<void>
  runCode?: (script: string, selectedData?: unknown) => void | Promise<void>
  reloadElement?: (
    elementName: string,
    data?: unknown,
    actionContext?: Record<string, unknown>,
  ) => void | Promise<void>
  getForm?: (args: Record<string, unknown>) => Record<string, unknown>
  collectLiveFieldOverrides?: (formId: string | number) => Record<string, unknown>
}

export function extractReloadElementNamesFromScript(script: string): string[] {
  const names: string[] = []
  const re = /en\.reloadElement\s*\(\s*["']([^"']+)["']/g
  let match: RegExpExecArray | null
  while ((match = re.exec(script)) != null) {
    const name = String(match[1] ?? '').trim()
    if (name && !names.includes(name)) names.push(name)
  }
  return names
}

function buildDialogCloseReloadPayload(
  formProvider: DialogCloseFormProvider,
  options: {
    formId?: string
    panelId?: string | number
    parentPageData?: Record<string, unknown>
  },
): Record<string, unknown> {
  const formId = String(options.formId ?? '').trim()
  const panelId = options.panelId
  const pageData = options.parentPageData
  const pageFields =
    pageData?.uniData &&
    typeof pageData.uniData === 'object' &&
    (pageData.uniData as { fields?: { fields?: Record<string, unknown> } }).fields?.fields
      ? ((pageData.uniData as { fields: { fields: Record<string, unknown> } }).fields.fields)
      : {}
  const changedFields = resolveJsResultChangedDataFieldMap(
    (pageData?.jsResult as { changed_data?: unknown } | undefined)?.changed_data,
  )
  const panelForm =
    formId && formProvider.getForm
      ? formProvider.getForm({ formId, panelId, flatten: true }) || {}
      : {}
  const fullForm =
    formId && formProvider.getForm ? formProvider.getForm({ formId, flatten: true }) || {} : {}
  const live = formId && formProvider.collectLiveFieldOverrides
    ? formProvider.collectLiveFieldOverrides(formId)
    : {}
  return mergeLiveFieldOverridesIntoFlatFormData(
    mergePageFieldMaps(
      { shouldPreserveEmptyIncoming: () => true },
      pageFields,
      changedFields,
      panelForm,
      fullForm,
    ) as Record<string, { type: string; value: unknown }>,
    live as Record<string, { type: string; value: unknown }>,
  )
}

function resolveDialogCloseOnChangePayload(fieldData: unknown): unknown {
  return fieldData != null && typeof fieldData === 'object' && 'value' in (fieldData as object)
    ? (fieldData as { value: unknown }).value
    : fieldData
}

function resolveDialogCloseOnChangeElement(
  registered: Record<string, unknown> | null | undefined,
  options: DialogCloseFieldOnChangeContext,
): Record<string, unknown> | null {
  const fromOptions = options.element
  const fromRegistered = registered
  const element = (fromOptions ?? fromRegistered) as Record<string, unknown> | null | undefined
  if (!element || typeof element !== 'object') return null
  if (readRegisteredElementOnChangeScript(element)) return element
  if (fromRegistered && fromRegistered !== element && readRegisteredElementOnChangeScript(fromRegistered)) {
    return { ...element, ...fromRegistered }
  }
  return null
}

export async function dispatchDialogCloseFieldOnChange(
  formProvider: DialogCloseFormProvider | null | undefined,
  registered: Record<string, unknown> | null | undefined,
  fieldData: unknown,
  options: DialogCloseFieldOnChangeContext = {},
): Promise<boolean> {
  const elementMeta = resolveDialogCloseOnChangeElement(registered, options)
  const script = elementMeta ? readRegisteredElementOnChangeScript(elementMeta) : ''
  if (!script || !formProvider) return false

  const regCtx =
    registered && typeof registered.getContext === 'function'
      ? (registered.getContext as () => Record<string, unknown>)()
      : null
  const formId = String(
    options.formId ?? regCtx?.formId ?? registered?.formId ?? elementMeta?.formId ?? '',
  ).trim()
  const panelId = options.panelId ?? regCtx?.panelId ?? registered?.panelId ?? elementMeta?.panelId
  const elementId =
    options.elementId ?? regCtx?.elementId ?? registered?.elementId ?? elementMeta?.elementId
  const payload = resolveDialogCloseOnChangePayload(fieldData)
  const pageData = options.parentPageData ?? regCtx?.pageData
  const onChangeElement =
    (regCtx?.element as Record<string, unknown> | undefined) ?? elementMeta ?? registered

  const reloadTargets = extractReloadElementNamesFromScript(script)
  const reloadPayload = buildDialogCloseReloadPayload(formProvider, {
    formId,
    panelId: panelId as string | number | undefined,
    parentPageData: pageData as Record<string, unknown> | undefined,
  })
  const reloadContext = {
    formId,
    panelId,
    skipPageFocusGuard: true,
    source: 'dialogClose.fieldOnChange',
    pageDataSlot: options.parentStoreKey,
  }

  if (reloadTargets.length > 0 && typeof formProvider.reloadElement === 'function') {
    for (const tableName of reloadTargets) {
      await formProvider.reloadElement(tableName, reloadPayload, reloadContext)
    }
    return true
  }

  const onElementCtx: Record<string, unknown> = {
    ...(regCtx ?? {}),
    formId,
    panelId,
    elementId,
    element: onChangeElement,
    pageData,
    formPath: { formId, panelId, elementId },
  }

  if (typeof formProvider.onElement === 'function') {
    await formProvider.onElement(onElementCtx)
    return true
  }

  if (typeof formProvider.runAction === 'function' && formId && elementId != null) {
    await formProvider.runAction({
      data: onChangeElement,
      level: 'element',
      eventName: 'onChange',
      selectedData: payload,
      formId,
      panelId,
      elementId,
    })
    return true
  }

  if (typeof formProvider.runCode === 'function') {
    await formProvider.runCode(script, { value: payload, selectedData: payload })
    return true
  }

  return false
}

export async function flushDialogClosePendingFieldOnChanges(
  pending: DialogClosePendingFieldOnChange[],
  formProvider: DialogCloseFormProvider | null | undefined,
  appStore?: { pagesData?: Record<string, unknown> },
): Promise<void> {
  for (const item of pending) {
    const parentPageData =
      item.context.parentPageData ??
      (item.context.parentStoreKey
        ? (appStore?.pagesData?.[item.context.parentStoreKey] as Record<string, unknown> | undefined)
        : undefined)
    await dispatchDialogCloseFieldOnChange(formProvider, item.registered, item.fieldData, {
      ...item.context,
      fieldName: item.fieldName,
      parentPageData,
    })
  }
}

export function augmentDialogCloseConfigMergeScope(
  cfg: Record<string, unknown>,
  options: {
    scriptText?: unknown
    actionContext?: DialogCloseMergeScopeContext
    dependencies?: ActionDependencies
  } = {},
): Record<string, unknown> {
  const out = { ...cfg }
  const scriptText = options.scriptText ?? cfg.onCloseDialogEvent
  const inferred = inferDialogCloseMergeFieldKeysFromScript(scriptText)

  // dialogClosed явно задаёт setField/refillElement
  if (inferred.length) {
    out.onCloseDialogParentMergeFieldKeys = inferred
    out.onCloseDialogParentMergeLayoutLevel = 'element'
    return out
  }

  if (hasDialogCloseMergeFieldKeys(cfg)) return cfg

  if (options.actionContext) {
    const scope = resolveLayoutMergeKeysForGetData(options.actionContext, options.dependencies)
    if (scope.mergeFieldKeys?.length) {
      out.onCloseDialogParentMergeFieldKeys = [...scope.mergeFieldKeys]
      if (scope.layoutLevel) out.onCloseDialogParentMergeLayoutLevel = scope.layoutLevel
      return out
    }
    if (scope.strictEmptyParentMergeScope && scope.layoutLevel) {
      out.onCloseDialogParentMergeLayoutLevel = scope.layoutLevel
    }
  }

  return out
}

const MULTI_SELECT_FIELD_SUFFIXES = ['__listbox', '__items', '__action'] as const

function resolveRegistryFieldNameByElementId(
  dependencies: ActionDependencies | undefined,
  elementId: unknown,
): string | undefined {
  const id = String(elementId ?? '').trim()
  if (!id) return undefined
  const elements =
    dependencies?.formProvider?.getElements?.() ?? dependencies?.getElements?.()
  if (!elements || typeof elements !== 'object') return undefined

  const idNum = Number.parseInt(id, 10)
  for (const el of Object.values(elements) as Array<Record<string, unknown>>) {
    const elIdRaw = el?.elementId ?? (el?.ctx as { id?: unknown } | undefined)?.id
    const elId = String(elIdRaw ?? '').trim()
    const idMatch =
      elId === id || (Number.isFinite(idNum) && elIdRaw === idNum)
    if (!idMatch) continue
    const name = el?.fieldName ?? el?.name
    if (name != null && String(name).trim() !== '') {
      return String(name)
    }
  }
  return undefined
}

function hasNonemptyDialogCloseFields(
  cfg: Record<string, unknown> | undefined | null,
): boolean {
  const keysRaw = cfg?.onCloseDialogParentMergeFieldKeys
  return Array.isArray(keysRaw) && keysRaw.length > 0
}

function expandDialogCloseAllowlistKeySet(bases: Set<string>): Set<string> {
  const out = new Set(bases)
  for (const b of bases) {
    for (const suf of MULTI_SELECT_FIELD_SUFFIXES) {
      out.add(b + suf)
    }
  }
  return out
}

function dialogCloseMergeAllowBases(cfg: Record<string, unknown> | undefined | null): Set<string> {
  const keysRaw = cfg?.onCloseDialogParentMergeFieldKeys
  const bases = new Set<string>()
  MERGE_PASSTHROUGH_FIELD_KEYS.forEach((k) => bases.add(k))
  if (hasNonemptyDialogCloseFields(cfg) && Array.isArray(keysRaw)) {
    keysRaw.forEach((k) => bases.add(String(k)))
  }
  return bases
}

export function stripEnDataToDialogCloseMergeAllowlist(
  merged: Record<string, unknown>,
  cfg: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  if (!merged || typeof merged !== 'object' || Array.isArray(merged)) {
    return {}
  }
  const layoutLevel = cfg?.onCloseDialogParentMergeLayoutLevel

  if (
    !hasNonemptyDialogCloseFields(cfg) &&
    !isNarrowDialogParentMergeLayoutLevel(layoutLevel)
  ) {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(merged)) {
      if (!isDialogCloseParentMergeBlockedKey(k)) {
        out[k] = v
      }
    }
    return out
  }

  const bases = dialogCloseMergeAllowBases(cfg)
  const expanded = expandDialogCloseAllowlistKeySet(bases)
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(merged)) {
    if (expanded.has(k)) {
      out[k] = v
    }
  }
  return out
}

export function pickDialogCloseMergedFieldsForParentEnSeed(
  mergedCloseFields: Record<string, unknown>,
  cfg: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  if (!mergedCloseFields || typeof mergedCloseFields !== 'object' || Array.isArray(mergedCloseFields)) {
    return {}
  }
  const keysRaw = cfg?.onCloseDialogParentMergeFieldKeys
  const layoutLevel = cfg?.onCloseDialogParentMergeLayoutLevel

  if (hasNonemptyDialogCloseFields(cfg) && Array.isArray(keysRaw)) {
    const expanded = expandDialogCloseAllowlistKeySet(
      new Set([...keysRaw.map((k) => String(k)), ...MERGE_PASSTHROUGH_FIELD_KEYS]),
    )
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(mergedCloseFields)) {
      if (expanded.has(k)) out[k] = v
    }
    return out
  }

  if (isNarrowDialogParentMergeLayoutLevel(layoutLevel)) {
    const out: Record<string, unknown> = {}
    for (const k of MERGE_PASSTHROUGH_FIELD_KEYS) {
      const v = mergedCloseFields[k]
      if (v !== undefined) out[k] = v
    }
    return out
  }

  return { ...mergedCloseFields }
}

export type LayoutScopedFieldKeysResult = {
  fieldKeys: string[]
  layoutLevel: string
  eventsEmptyScope?: boolean
  strictEmptyMergeScope?: boolean
}

export function computeLayoutScopedFieldKeys(
  context: DialogCloseMergeScopeContext,
  dependencies?: ActionDependencies,
): LayoutScopedFieldKeysResult | undefined {
  const getForm = dependencies?.getForm
  if (!getForm) return undefined

  const host = layoutHost(context)
  const layout = host?.layout || {}
  let actualLevel = (layout.level?._text || context.level || 'form') as string
  if (typeof actualLevel === 'string') {
    actualLevel = actualLevel.toLowerCase()
  }

  const layoutPosition = normalizeLayoutText(layout.position?._text ?? layout.position)
  if (layoutPosition === 'events') {
    return {
      fieldKeys: [],
      layoutLevel: actualLevel || 'form',
      eventsEmptyScope: true,
    }
  }

  const layoutId = layout.id?._text ?? layout.id
  const contextFormId = context.formId ? parseInt(String(context.formId), 10) : 0
  const targetFormId =
    actualLevel === 'form' && layoutId != null && String(layoutId).trim() !== ''
      ? parseInt(String(layoutId), 10)
      : contextFormId

  let slice: Record<string, any> = {}

  switch (actualLevel) {
    case 'element': {
      if (context.elementId || layoutId != null) {
        const elementId = layoutId ?? context.elementId
        slice =
          getForm({
            formId: targetFormId,
            panelId: context.panelId,
            elementId,
            flatten: false,
          }) || {}
      }
      break
    }
    case 'panel': {
      if (layoutId != null || context.panelId) {
        const panelId = layoutId ?? context.panelId
        slice = getForm({ formId: targetFormId, panelId, flatten: false }) || {}
      } else {
        slice = getForm({ formId: targetFormId, flatten: false }) || {}
      }
      break
    }
    case 'form':
      slice = getForm({ formId: targetFormId, flatten: false }) || {}
      break
    case 'page': {
      const getPageData = dependencies?.getPageData
      const pageId = layoutId ?? context.pageId
      if (pageId && getPageData) {
        slice = getPageData({ formId: targetFormId, pageId, flatten: false }) || {}
      } else {
        slice = getForm({ formId: targetFormId, flatten: false }) || {}
      }
      break
    }
    default:
      slice = getForm({ formId: targetFormId, flatten: false }) || {}
  }

  const fieldKeys = Object.keys(slice).filter((k) => slice[k] !== undefined)
  if (fieldKeys.length === 0) {
    const hadNarrowPath =
      (actualLevel === 'element' && (context.elementId != null || layoutId != null)) ||
      (actualLevel === 'panel' && (layoutId != null || context.panelId)) ||
      (actualLevel === 'page' && (layoutId != null || context.pageId))
    if (hadNarrowPath) {
      if (actualLevel === 'element') {
        const elementId = layoutId ?? context.elementId
        const registryField = resolveRegistryFieldNameByElementId(dependencies, elementId)
        if (registryField) {
          return {
            fieldKeys: [registryField],
            layoutLevel: actualLevel,
          }
        }
      }
      return {
        fieldKeys: [],
        layoutLevel: actualLevel,
        strictEmptyMergeScope: true,
      }
    }
    return undefined
  }

  return { fieldKeys, layoutLevel: actualLevel }
}

export function resolveLayoutMergeKeysForGetData(
  context: DialogCloseMergeScopeContext,
  dependencies?: ActionDependencies,
): {
  mergeFieldKeys: string[] | undefined
  eventsEmptyMergeScope?: boolean
  strictEmptyParentMergeScope?: boolean
  layoutLevel?: string
} {
  const scope = computeLayoutScopedFieldKeys(context, dependencies)
  if (!scope) {
    return { mergeFieldKeys: undefined }
  }
  if (scope.eventsEmptyScope) {
    return {
      mergeFieldKeys: undefined,
      eventsEmptyMergeScope: true,
      layoutLevel: scope.layoutLevel,
    }
  }
  if (scope.strictEmptyMergeScope) {
    return {
      mergeFieldKeys: [],
      layoutLevel: scope.layoutLevel,
      strictEmptyParentMergeScope: true,
    }
  }
  if (!scope.fieldKeys?.length) {
    return { mergeFieldKeys: undefined, layoutLevel: scope.layoutLevel }
  }
  return {
    mergeFieldKeys: [...scope.fieldKeys],
    layoutLevel: scope.layoutLevel,
  }
}

export const computeOnCloseDialogParentMergeFieldKeys = computeLayoutScopedFieldKeys
export const computeLayoutScopedFieldKeysForGetData = computeLayoutScopedFieldKeys

export function pickDialogCloseParentMergeFlatFields(
  flat: Record<string, any>,
  parentMergeFieldKeys: string[] | undefined | null,
  _states: StateData[],
): Record<string, any> {
  if (parentMergeFieldKeys === undefined || parentMergeFieldKeys === null) {
    return flat
  }
  if (parentMergeFieldKeys.length === 0) {
    const out: Record<string, any> = {}
    for (const [k, v] of Object.entries(flat)) {
      if (MERGE_PASSTHROUGH_FIELD_KEYS.has(k)) {
        out[k] = v
      }
    }
    return out
  }
  const expanded = expandDialogCloseAllowlistKeySet(new Set(parentMergeFieldKeys))
  MERGE_PASSTHROUGH_FIELD_KEYS.forEach((k) => expanded.add(k))
  const out: Record<string, any> = {}
  for (const [k, v] of Object.entries(flat)) {
    if (expanded.has(k)) {
      out[k] = v
    }
  }
  return out
}

export function getDialogCloseChangedFieldsFlat(changedData: unknown): Record<string, any> {
  if (!changedData || typeof changedData !== 'object' || Array.isArray(changedData)) return {}
  const o = changedData as Record<string, any>
  return mergePageFieldMaps(
    resolveClientDataActualFields({ fields: o.clientData || {} }),
    resolveJsResultChangedDataFieldMap(o.changed_data ?? o),
  )
}

function wrapFilteredDialogClosePayload(
  changedData: Record<string, any>,
  outRaw: Record<string, any>,
  useChangedDataBranch: boolean,
): Record<string, any> {
  const { clientData: _omitClient, ...rest } = changedData
  if (useChangedDataBranch) {
    return {
      ...rest,
      changed_data: {
        fields: {
          fields: outRaw,
        },
      },
    }
  }
  return {
    ...rest,
    fields: {
      fields: outRaw,
    },
  }
}

export function filterDialogCloseChangedDataForParentMerge(
  changedData: any,
  parentMergeFieldKeys: string[] | undefined | null,
  states: StateData[],
  options?: { eventsEmptyScope?: boolean },
): any {
  if (options?.eventsEmptyScope) {
    if (!changedData || typeof changedData !== 'object') {
      return changedData
    }
    const raw = getDialogCloseChangedFieldsFlat(changedData)
    if (Object.keys(raw).length === 0) return changedData
    const outRaw: Record<string, any> = {}
    for (const k of Object.keys(raw)) {
      if (MERGE_PASSTHROUGH_FIELD_KEYS.has(k)) {
        outRaw[k] = raw[k]
      }
    }
    if (Object.keys(outRaw).length === Object.keys(raw).length) {
      let same = true
      for (const k of Object.keys(raw)) {
        if (outRaw[k] !== raw[k]) {
          same = false
          break
        }
      }
      if (same) return changedData
    }
    if (
      changedData.changed_data &&
      typeof changedData.changed_data === 'object' &&
      changedData.changed_data.fields &&
      typeof changedData.changed_data.fields === 'object'
    ) {
      return wrapFilteredDialogClosePayload(changedData, outRaw, true)
    }
    return wrapFilteredDialogClosePayload(changedData, outRaw, false)
  }

  if (parentMergeFieldKeys === undefined || parentMergeFieldKeys === null) {
    return changedData
  }
  if (parentMergeFieldKeys.length === 0) {
    if (!changedData || typeof changedData !== 'object') {
      return changedData
    }
    const raw = getDialogCloseChangedFieldsFlat(changedData)
    if (Object.keys(raw).length === 0) return changedData
    const outRaw: Record<string, any> = {}
    for (const k of Object.keys(raw)) {
      if (MERGE_PASSTHROUGH_FIELD_KEYS.has(k)) {
        outRaw[k] = raw[k]
      }
    }
    if (Object.keys(outRaw).length === Object.keys(raw).length) {
      let same = true
      for (const k of Object.keys(raw)) {
        if (outRaw[k] !== raw[k]) {
          same = false
          break
        }
      }
      if (same) return changedData
    }
    if (
      changedData.changed_data &&
      typeof changedData.changed_data === 'object' &&
      changedData.changed_data.fields &&
      typeof changedData.changed_data.fields === 'object'
    ) {
      return wrapFilteredDialogClosePayload(changedData, outRaw, true)
    }
    return wrapFilteredDialogClosePayload(changedData, outRaw, false)
  }
  if (!changedData || typeof changedData !== 'object') {
    return changedData
  }

  const raw = getDialogCloseChangedFieldsFlat(changedData)
  if (Object.keys(raw).length === 0) return changedData

  const outRaw = pickDialogCloseParentMergeFlatFields(raw, parentMergeFieldKeys, states)

  if (Object.keys(outRaw).length === Object.keys(raw).length) {
    let same = true
    for (const k of Object.keys(raw)) {
      if (outRaw[k] !== raw[k]) {
        same = false
        break
      }
    }
    if (same) return changedData
  }

  if (
    changedData.changed_data &&
    typeof changedData.changed_data === 'object' &&
    changedData.changed_data.fields &&
    typeof changedData.changed_data.fields === 'object'
  ) {
    return wrapFilteredDialogClosePayload(changedData, outRaw, true)
  }

  return wrapFilteredDialogClosePayload(changedData, outRaw, false)
}
